import { useEffect, useState, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import PurchaseInvoiceFormDialog from "@/components/dialogs/PurchaseInvoiceFormDialog";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Download, Pencil, Trash2, Search, Receipt, Wallet, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const currency = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function PurchaseInvoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/purchase-invoices").then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.toLowerCase();
    return invoices.filter((i) => i.vendor_name.toLowerCase().includes(q) || i.invoice_number.toLowerCase().includes(q));
  }, [invoices, search]);

  const totals = useMemo(() => {
    const unpaid = invoices.filter((i) => i.status === "Unpaid").reduce((s, i) => s + i.total_amount, 0);
    const overdue = invoices.filter((i) => i.status === "Overdue").length;
    const totalThisYear = invoices.filter((i) => i.bill_date?.startsWith(String(new Date().getFullYear()))).reduce((s, i) => s + i.total_amount, 0);
    return { unpaid, overdue, totalThisYear };
  }, [invoices]);

  const deleteInvoice = async (id) => {
    try {
      await api.delete(`/purchase-invoices/${id}`);
      toast.success("Purchase invoice deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Purchase Invoices">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="purchase-invoice-summary-cards">
        <SummaryCard icon={Wallet} label="Unpaid Total" value={currency(totals.unpaid)} testId="card-unpaid-total" />
        <SummaryCard icon={AlertTriangle} label="Overdue Bills" value={totals.overdue} testId="card-overdue-count" />
        <SummaryCard icon={Receipt} label="Total This Year" value={currency(totals.totalThisYear)} testId="card-year-total" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <Input data-testid="purchase-invoice-search-input" placeholder="Search vendor or invoice #..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 w-64 h-9" />
        </div>
        <div className="flex-1" />
        <Button size="sm" data-testid="add-purchase-invoice-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Record Purchase Invoice
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto" data-testid="purchase-invoices-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-4 py-3 font-medium">Vendor</th>
              <th className="px-4 py-3 font-medium">Invoice #</th>
              <th className="px-4 py-3 font-medium">Bill Date</th>
              <th className="px-4 py-3 font-medium">GST</th>
              <th className="px-4 py-3 font-medium">Total</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">File</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#4B5563]">No purchase invoices recorded yet.</td></tr>}
            {filtered.map((inv) => (
              <tr key={inv.id} data-testid={`purchase-invoice-row-${inv.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                <td className="px-4 py-3 font-medium text-[#1C1C1E]">{inv.vendor_name}</td>
                <td className="px-4 py-3 text-[#4B5563]">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{inv.bill_date}</td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">{inv.gst_type && inv.gst_type !== "None" ? inv.gst_type : "—"}</td>
                <td className="px-4 py-3 font-medium text-[#1C1C1E]">{currency(inv.total_amount)}</td>
                <td className="px-4 py-3"><StatusBadge value={inv.status} /></td>
                <td className="px-4 py-3">
                  {inv.file_path ? (
                    <div className="flex items-center gap-2.5">
                      <a href={fileUrl(inv.file_path)} target="_blank" rel="noreferrer" data-testid={`purchase-invoice-file-view-${inv.id}`} title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                      <a href={downloadUrl(inv.file_path, inv.original_filename)} data-testid={`purchase-invoice-file-download-${inv.id}`} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                    </div>
                  ) : <span className="text-xs text-[#4B5563]">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button data-testid={`edit-purchase-invoice-${inv.id}`} onClick={() => { setEditing(inv); setFormOpen(true); }} className="text-[#4B5563] hover:text-[#1C1C1E]"><Pencil className="h-4 w-4" /></button>
                    {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-purchase-invoice-${inv.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete invoice from "{inv.vendor_name}"?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteInvoice(inv.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PurchaseInvoiceFormDialog open={formOpen} onOpenChange={setFormOpen} invoice={editing} onSaved={load} />
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-start justify-between">
      <div>
        <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-1">{label}</p>
        <p className="text-xl font-bold text-[#1C1C1E]">{value}</p>
      </div>
      <div className="h-9 w-9 rounded-md bg-[#76C121]/10 flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-[#00823B]" />
      </div>
    </div>
  );
}
